// Members' settings for tenant admins (WI12): the forms, the parents' consent messages, Medulance's
// registration, payments and GST invoices, and the tasks' lists, saved with the rest of the settings;
// then plans and prices, automated writers' keys, and the sheet's one-time import.
import { useEffect, useState } from "react";

import { api, send, upload } from "./api";
import { lines, money, split, toMinor } from "./format";
import { useAct } from "./Members";
import { usePoll } from "./Pages";
import type { Billing, Lists, Plan, Writer } from "./types";

type Settings = Record<string, Record<string, unknown>>;
type Obj = Record<string, unknown>;

const obj = (value: unknown): Obj => (value && typeof value === "object" && !Array.isArray(value) ? (value as Obj) : {});
const LANGUAGES: [string, string][] = [
  ["en", "English"],
  ["hi", "Hindi"],
  ["hinglish", "Hinglish"],
];
const FORM_LINKS: [string, string, string][] = [
  ["onboarding", "Onboarding", "https://onboarding.tellniro.com/start"],
  ["lite", "Lite", "https://onboarding.tellniro.com/lite"],
  ["assure", "Assure", "https://assure.tellniro.com/start"],
];

export function MemberSettings({ settings, onChange }: { settings: Settings; onChange: (next: Settings) => void }) {
  const [lists, setLists] = useState<Lists | null>(null);
  useEffect(() => {
    api<Lists>("/api/lists").then(setLists).catch(() => setLists(null));
  }, []);
  const m = obj(settings.members);
  const b = obj(settings.billing);
  const setMembers = (key: string, value: Obj) => onChange({ ...settings, members: { ...m, [key]: { ...obj(m[key]), ...value } } });
  const setBilling = (value: Obj) => onChange({ ...settings, billing: { ...b, ...value } });
  const trial = obj(settings.trial);
  const forms = obj(m.forms);
  const consent = obj(m.consent);
  const medulance = obj(m.medulance);
  const invoices = obj(b.invoices);
  const taxes = obj(invoices.taxes);
  const setInvoices = (value: Obj) => setBilling({ invoices: { ...invoices, ...value } });
  const setTax = (currency: string, value: Obj) => setInvoices({ taxes: { ...taxes, [currency]: { ...obj(taxes[currency]), ...value } } });
  const ownLists = obj(m.lists);

  return (
    <>
      <h2>Onboarding forms</h2>
      <label className="check">
        <input type="checkbox" checked={Boolean(forms.enabled)} onChange={(e) => setMembers("forms", { enabled: e.target.checked })} />
        Take submissions from Tally (onboarding, Lite and Assure; switch on before adding the webhook in Tally, off to refuse it)
      </label>
      <p className="muted">
        Tally serves the forms and sends each submission here; their questions and wording are edited in Tally. A changed question still
        arrives, but sets off the error alarm until the platform knows it. Open:{" "}
        {FORM_LINKS.map(([key, name, url], i) => (
          <span key={key}>
            {i > 0 && ", "}
            <a href={String(obj(forms.urls)[key] ?? url)} target="_blank" rel="noopener">
              {name}
            </a>
          </span>
        ))}
        .
      </p>

      <h2>Parents' consent messages</h2>
      <p className="muted">
        A WhatsApp template with each parent's own consent link, in their language. Name each template once Meta has approved it in WATI,
        then switch the messages on. Until then ops record a consent given on a call.
      </p>
      <label className="check">
        <input type="checkbox" checked={Boolean(consent.enabled)} onChange={(e) => setMembers("consent", { enabled: e.target.checked })} />
        Send consent messages (after an Assure form, and from a member's page)
      </label>
      {LANGUAGES.map(([code, name]) => (
        <label key={code}>
          {name} template's name in WATI
          <input
            value={String(obj(consent.templates)[code] ?? "")}
            onChange={(e) => setMembers("consent", { templates: { ...obj(consent.templates), [code]: e.target.value.trim() } })}
          />
        </label>
      ))}
      <div className="grid2">
        <label>
          The link goes in
          <select value={String(consent.link_in ?? "button")} onChange={(e) => setMembers("consent", { link_in: e.target.value })}>
            <option value="button">the button (its URL ends with the link's secret)</option>
            <option value="body">the message, as {"{{3}}"}</option>
          </select>
        </label>
        <label>
          The button's parameter in WATI
          <input value={String(consent.button_param ?? "button_url")} onChange={(e) => setMembers("consent", { button_param: e.target.value.trim() })} />
        </label>
        <label>
          A link lasts (days)
          <input
            inputMode="numeric"
            value={String(consent.days ?? 30)}
            onChange={(e) => setMembers("consent", { days: Number(e.target.value.replace(/\D/g, "")) || 30 })}
          />
        </label>
      </div>
      {Object.entries(obj(consent.template_texts)).map(([name, body]) => (
        <div key={name} className="template-setting">
          <strong>{name}</strong> <span className="muted">button: {String(obj(consent.template_buttons)[name] ?? "")}</span>
          <textarea
            rows={3}
            value={String(body)}
            onChange={(e) => setMembers("consent", { template_texts: { ...obj(consent.template_texts), [name]: e.target.value } })}
          />
        </div>
      ))}

      <h2>Medulance</h2>
      <label className="check">
        <input type="checkbox" checked={Boolean(medulance.enabled)} onChange={(e) => setMembers("medulance", { enabled: e.target.checked })} />
        Register parents with Medulance when a family activates Assure (needs Medulance's production key, TD94)
      </label>

      <h2>Payments</h2>
      <label className="check">
        <input type="checkbox" checked={Boolean(b.enabled)} onChange={(e) => setBilling({ enabled: e.target.checked })} />
        Take payments through xPay (needs its keys: live ones in production, sandbox ones on dev, where with
        none a stand-in moves no money)
      </label>
      <label className="check">
        <input
          type="checkbox"
          checked={trial.enabled !== false}
          onChange={(e) => onChange({ ...settings, trial: { ...trial, enabled: e.target.checked } })}
        />
        The 30-day trial's card step on the site (needs payments on; when off, the site shows its WhatsApp link instead)
      </label>
      <label className="check">
        <input type="checkbox" checked={Boolean(b.pass_through)} onChange={(e) => setBilling({ pass_through: e.target.checked })} />
        Pass-through payments (built; off until Akshat says how they should work)
      </label>
      <h3>GST invoices</h3>
      <p className="muted">Off until the founders' accountant confirms every rule below.</p>
      <label className="check">
        <input type="checkbox" checked={Boolean(invoices.enabled)} onChange={(e) => setInvoices({ enabled: e.target.checked })} />
        Issue an invoice for each captured payment
      </label>
      <div className="grid2">
        {(
          [
            ["gstin", "GSTIN"],
            ["legal_name", "Legal name"],
            ["sac", "SAC code"],
            ["series", "Number series (prefix, e.g. NIRO/26-27/)"],
          ] as [string, string][]
        ).map(([key, label]) => (
          <label key={key}>
            {label}
            <input value={String(invoices[key] ?? "")} onChange={(e) => setInvoices({ [key]: e.target.value })} />
          </label>
        ))}
      </div>
      <label>
        Address on the invoice
        <textarea rows={2} value={String(invoices.address ?? "")} onChange={(e) => setInvoices({ address: e.target.value })} />
      </label>
      <label className="check">
        <input
          type="checkbox"
          checked={invoices.prices_include_tax === undefined ? true : Boolean(invoices.prices_include_tax)}
          onChange={(e) => setInvoices({ prices_include_tax: e.target.checked })}
        />
        Prices include the tax
      </label>
      {["INR", "USD"].map((currency) => {
        const t = obj(taxes[currency]);
        return (
          <div key={currency} className="grid3">
            <label>
              {currency}: rate (%)
              <input inputMode="decimal" value={String(t.rate ?? "")} onChange={(e) => setTax(currency, { rate: Number(e.target.value) || 0 })} />
            </label>
            <label>
              Tax line's label
              <input value={String(t.label ?? "")} placeholder={currency === "USD" ? "Zero-rated (LUT)" : "IGST 18%"} onChange={(e) => setTax(currency, { label: e.target.value })} />
            </label>
            <label>
              Note on the invoice
              <input value={String(t.note ?? "")} onChange={(e) => setTax(currency, { note: e.target.value })} />
            </label>
          </div>
        );
      })}

      <h2>Tasks' lists</h2>
      <p className="muted">The choices for tasks (the sheet's Lists tab): one a line.</p>
      <div className="grid3">
        {(
          [
            ["statuses", "Statuses"],
            ["categories", "Categories"],
            ["initiators", "Initiators"],
          ] as [keyof Lists, string][]
        ).map(([key, label]) => (
          <label key={key}>
            {label}
            <textarea
              rows={6}
              value={lines(ownLists[key] ?? lists?.[key] ?? [])}
              onChange={(e) => setMembers("lists", { [key]: split(e.target.value) })}
            />
          </label>
        ))}
      </div>
    </>
  );
}

export function PlansAdmin() {
  const { data, error, reload } = usePoll(() => api<Billing>("/api/billing"), 600000);
  const { act, problem } = useAct(reload);
  const blank = { code: "", name: "", kind: "membership", period: "month", currency: "USD", amount: "", active: true };
  const [draft, setDraft] = useState<typeof blank | null>(null);
  const edit = (p: Plan) =>
    setDraft({ code: p.code, name: p.name, kind: p.kind, period: p.period ?? "", currency: p.currency, amount: String(p.amount_minor / 100), active: p.active });
  const save = async () => {
    if (!draft) {
      return;
    }
    const body = { ...draft, period: draft.kind === "service" ? null : draft.period || null, amount_minor: toMinor(draft.amount) };
    if ((await act(() => send("/api/billing/plans", body, "PUT"))) !== null) {
      setDraft(null);
    }
  };
  return (
    <div className="settings">
      <h2>Plans and prices</h2>
      <p className="muted">A changed price makes a new plan at the provider; members already subscribed keep theirs.</p>
      {error && <p className="note warn">{error}</p>}
      {problem && <p className="note warn">{problem}</p>}
      {data?.plans.map((p) => (
        <p key={p.id} className={p.active ? "" : "muted"}>
          {p.name}: {money(p.amount_minor, p.currency)}
          {p.period ? ` a ${p.period}` : ""} <span className="muted">({p.code}{p.active ? "" : ", not offered"})</span>{" "}
          <button className="link" onClick={() => edit(p)}>
            edit
          </button>
        </p>
      ))}
      {data && data.plans.length === 0 && (
        <button className="secondary" onClick={() => act(() => send("/api/billing/plans/seed"))}>
          Add the website's prices
        </button>
      )}
      {draft ? (
        <div>
          <div className="grid2">
            <label>
              Code (letters, digits, dashes)
              <input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value.toLowerCase() })} />
            </label>
            <label>
              Name
              <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </label>
            <label>
              Kind
              <select value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value })}>
                <option value="membership">membership</option>
                <option value="lite">lite</option>
                <option value="assure">assure</option>
                <option value="service">service (one-off)</option>
              </select>
            </label>
            <label>
              Renews every
              <select value={draft.period} disabled={draft.kind === "service"} onChange={(e) => setDraft({ ...draft, period: e.target.value })}>
                <option value="month">month</option>
                <option value="quarter">quarter</option>
                <option value="half_year">six months</option>
                <option value="year">year</option>
              </select>
            </label>
            <label>
              Currency
              <select value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })}>
                <option>USD</option>
                <option>INR</option>
              </select>
            </label>
            <label>
              Price
              <input inputMode="decimal" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} />
            </label>
          </div>
          <label className="check">
            <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} />
            Offered
          </label>
          <div className="actions">
            <button disabled={!draft.code || !draft.name || !(toMinor(draft.amount) > 0)} onClick={save}>
              Save the plan
            </button>
            <button className="secondary" onClick={() => setDraft(null)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button className="secondary" onClick={() => setDraft(blank)}>
          Add a plan
        </button>
      )}
    </div>
  );
}

export function WritersAdmin() {
  const { data, error, reload } = usePoll(() => api<Writer[]>("/api/writers"), 600000);
  const { act, problem } = useAct(reload);
  const [name, setName] = useState("");
  const [key, setKey] = useState("");
  return (
    <div className="settings">
      <h2>Automated writers</h2>
      <p className="muted">
        A key for something that updates tasks from chats (the sheet's Auto log): it sends <code>Authorization: Writer &lt;key&gt;</code> to{" "}
        <code>/api/writer/tasks</code>, with the chat and the line it read. The key is shown once.
      </p>
      {error && <p className="note warn">{error}</p>}
      {problem && <p className="note warn">{problem}</p>}
      {key && (
        <p className="note">
          The new key, shown once: <code>{key}</code>{" "}
          <button className="link" onClick={() => setKey("")}>
            done
          </button>
        </p>
      )}
      {data?.map((w) => (
        <p key={w.id} className={w.revoked_at ? "muted" : ""}>
          {w.name} {w.revoked_at ? "(revoked)" : ""}{" "}
          {!w.revoked_at && (
            <button className="link" onClick={() => window.confirm(`Revoke ${w.name}'s key?`) && act(() => send(`/api/writers/${w.id}/revoke`))}>
              revoke
            </button>
          )}
        </p>
      ))}
      <div className="actions">
        <input className="grow" placeholder="its name: auto-log" value={name} onChange={(e) => setName(e.target.value.toLowerCase())} />
        <button
          disabled={!/^[a-z0-9][a-z0-9-]{1,40}$/.test(name)}
          onClick={async () => {
            const out = await act(() => send<{ key: string }>("/api/writers", { name }));
            if (out) {
              setKey(out.key);
              setName("");
            }
          }}
        >
          Make a key
        </button>
      </div>
    </div>
  );
}

const TABS: [string, string][] = [
  ["onboarding", "Onboarding Form"],
  ["lite", "Lite Onboarding Form"],
  ["assure", "Assure Activation form"],
  ["dashboard", "Member Dashboard"],
  ["member_tasks", "Member tasks"],
  ["free_tasks", "Free tasks"],
  ["payments", "Consump Payments"],
  ["internal_ais", "Internal AIs"],
  ["lists", "Lists"],
  ["auto_log", "Auto log"],
];

type Report = {
  tab: string;
  name: string;
  rows: number;
  imported: number;
  already: number;
  skipped: string[];
  skipped_count: number;
  statuses: Record<string, number>;
};

export function ImportSheet() {
  const [files, setFiles] = useState<Record<string, File>>({});
  const [currency, setCurrency] = useState("INR");
  const [result, setResult] = useState<{ dry_run: boolean; tabs: Report[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");
  const run = async (dry: boolean) => {
    const form = new FormData();
    Object.entries(files).forEach(([tab, file]) => form.append(tab, file));
    form.append("dry_run", dry ? "1" : "0");
    form.append("currency", currency);
    setBusy(true);
    setProblem("");
    try {
      setResult(await upload<{ dry_run: boolean; tabs: Report[] }>("/api/members/import", form));
    } catch (e) {
      setProblem(String((e as Error).message));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="settings">
      <h2>Import the sheet</h2>
      <p className="muted">
        Each tab of "Niro Tasks Manager" as CSV (File, Download, CSV). A dry run first: it shows exactly what the import would do and keeps
        nothing. Running it again later adds only what's new.
      </p>
      <div className="grid2">
        {TABS.map(([tab, name]) => (
          <label key={tab}>
            {name}
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                const file = e.target.files?.[0];
                const next = { ...files };
                if (file) {
                  next[tab] = file;
                } else {
                  delete next[tab];
                }
                setFiles(next);
                setResult(null); // a changed file needs its own dry run
              }}
            />
          </label>
        ))}
      </div>
      <label>
        Currency of the payments tab
        <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
          <option>INR</option>
          <option>USD</option>
        </select>
      </label>
      <div className="actions">
        <button disabled={busy || Object.keys(files).length === 0} onClick={() => run(true)}>
          Dry run
        </button>
        <button
          className="secondary"
          disabled={busy || !result?.dry_run}
          onClick={() => window.confirm("Import these tabs for real?") && run(false)}
        >
          Import
        </button>
        {busy && <span className="muted">Working…</span>}
      </div>
      {problem && <p className="note warn">{problem}</p>}
      {result && (
        <>
          <p className="note">{result.dry_run ? "Dry run: nothing was kept." : "Imported."}</p>
          <table className="list">
            <thead>
              <tr>
                <th>Tab</th>
                <th>Rows</th>
                <th>{result.dry_run ? "Would import" : "Imported"}</th>
                <th>Already in</th>
                <th>Skipped</th>
              </tr>
            </thead>
            <tbody>
              {result.tabs.map((r) => (
                <tr key={r.tab}>
                  <td>{r.name}</td>
                  <td>
                    {r.rows}
                    {Object.entries(r.statuses ?? {}).map(([status, count]) => (
                      <div key={status} className="muted">
                        {status}: {count}
                      </div>
                    ))}
                  </td>
                  <td>{r.imported}</td>
                  <td>{r.already}</td>
                  <td>
                    {r.skipped_count}
                    {r.skipped.slice(0, 5).map((s) => (
                      <div key={s} className="muted">
                        {s}
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
